<template>
  <div class="comment in-list d-flex flex-sm-column flex-md-row" :class="commentClasses">
    <author :user="visibleComment.user" :createdDate="visibleComment.created" />
    <div class="flex-md-grow-1 content">
      <template v-if="showEditForm">
        <comment-form
          :thoughtid="visibleComment.thought.id"
          :topicid="topic.id"
          :commentid="visibleComment.commentid"
          :comment="visibleComment"
          @comment-form-close="toggleEdit()"
          @comment-updated="onCommentEdited"
        />
      </template>
      <div v-else>
        <div class="controls float-end">
          <span v-if="showCheckedStatus" style="margin-right: 10px" v-html="statusHtmlContent"></span>
          <div class="d-inline" v-if="canEditComment(visibleComment)">
            <button
              class="btn btn-sm btn-link"
              v-tooltip="'Bearbeiten'"
              @click="toggleEdit()"
              v-tooltip.bottom="'Bearbeiten'"
            >
              <font-awesome-icon icon="pencil-alt" aria-hidden="true" />
              <span class="visually-hidden">Bearbeiten</span>
            </button>
          </div>
        </div>
        <p v-dompurify-html="visibleComment.content"></p>
        <div class="attachment-list">
          <attachments v-if="comment.files.length > 0" :files="comment.files" />
        </div>
        <template v-if="visibleComment.last_update !== visibleComment.created">
          <p class="small mt-3 fst-italic">
            - Bearbeitet:
            {{ formattedDate(UTCtoLocalTime(visibleComment.last_update)) }}
          </p>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { onBeforeMount, ref, computed } from 'vue';

// stores
import { userStore } from '@/store/user-store';
import { domainStore } from '@/store/domain-store';

// components
import Author from '@/components/forum/elements/Author.vue';
import CommentForm from '@/components/forum/elements/CommentForm.vue';
import Attachments from '@/components/general/Attachments.vue';

// composables
import useTopicConfig from '@/composables/topic/useTopicConfig';
import useConverter from '@/composables/general/useConverter';

export default {
  name: 'forum-comment-in-list',
  props: {
    comment: {
      type: Object,
      required: true,
    },
    topic: {
      type: Object,
      required: true,
    },
  },

  setup(props, context) {
    const showEditForm = ref(false);
    const visibleComment = ref();
    const { canEditComment } = useTopicConfig();
    const { formattedDate, UTCtoLocalTime } = useConverter();

    //
    //-----------------COMPUTED------------------
    //
    const isLoggedIn = computed(() => {
      return userStore.isLoggedIn();
    });
    const domainid = computed(() => {
      return domainStore.getState().domainid;
    });
    const commentClasses = computed(() => {
      return {
        ['comment-' + props.comment.commentid]: true,
        highlighted: visibleComment.value.highlight ? true : false,
      };
    });
    const statusHtmlContent = computed(() => {
      // only show content information for comments that are not visible
      // TODO check in with PM?
      if (visibleComment.value.status != 'show') {
        if (visibleComment.value.content_checked == 'ok') {
          return '<span class="badge bg-success">Geprüft</span>';
        } else if (visibleComment.value.content_checked == 'unchecked') {
          return '<span class="badge bg-warning">Ungeprüft</span>';
        } else if (visibleComment.value.content_checked == 'misuse') {
          return '<span class="badge bg-danger">Versteckt</span>';
        }
      }
      return '';
    });
    const showCheckedStatus = computed(() => {
      return canEditComment(visibleComment.value) && statusHtmlContent.value !== '';
    });

    //
    //-----------------METHODS------------------
    //
    function toggleEdit() {
      showEditForm.value = !showEditForm.value;
      if (showEditForm.value) {
        // reset any messages on starting new edit
        context.emit('clear-messages');
      }
    }

    function onCommentEdited(comment) {
      visibleComment.value = comment;
      //new thought was created, go back to list view
      if (comment && comment.status === 'show') {
        context.emit('push-message', {
          severity: 'success',
          content: 'Beitrag wurde erfolgreich erstellt.',
        });
      } else {
        context.emit('push-message', {
          severity: 'success',
          content: 'Beitrag wird noch geprüft',
        });
      }
      showEditForm.value = false;
    }

    onBeforeMount(() => {
      visibleComment.value = props.comment;
    });

    return {
      // REFS
      showEditForm,
      visibleComment,

      // COMPUTED
      domainid,
      isLoggedIn,
      commentClasses,
      showCheckedStatus,
      statusHtmlContent,

      // METHODS
      canEditComment,
      formattedDate,
      UTCtoLocalTime,
      toggleEdit,
      onCommentEdited,
    };
  },

  components: {
    Author,
    CommentForm,
    Attachments,
  },
};
</script>

<style scoped lang="scss">
.comment.in-list {
  border-top: solid 1px #eee;
}
.comment .content {
  padding: 10px;
  white-space: pre-wrap;
}
</style>
